import type { AppointmentDetail, AppointmentStatus, ChartData, ChartField, ChartItem, ChartView, CheckoutView, DeviceView } from "@vaulted/shared";
import { BoulevardReceipt } from "../components/BoulevardReceipt";
import { PAYMENT_METHOD_LABELS, STATUS_LABELS, VITAL_RANGES, ageOn, formatCents, gfeOk } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { EditAppointment, STATUS_TONE, VisitAlerts, VisitDetails, VisitEndActions, VisitHistory, VisitMoney, time12 } from "../components/AppointmentDialog";
import { HealthBlock } from "../components/HealthBlock";
import { LineControls, SwapDrip, TreatmentAdds, useTreatmentEdits } from "../components/TreatmentEdit";
import { ExcuseNotesList, NoteDialog } from "../components/ExcuseNotes";
import { GfeVisitSection } from "../components/Gfe";
import { type AddedTracking, type TreatmentLine, TreatmentDrawing, treatmentLines, visitItemIds } from "../components/TreatmentDrawing";
import { Alert, Badge, Button, Field, Loading, cx, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import { useCatalog } from "./Catalog";
import { FieldView } from "./Chart";
import { displayName } from "./Clients";
import { BackLink, useBackTitle } from "../components/BackLink";

export type StepKey = "forms" | "gfe" | "pre" | "treatment" | "post" | "chart" | "review" | "paid";
export interface VisitStep {
  key: StepKey;
  label: string;
  state: "done" | "now" | "later";
  detail: string;
}

/** Vitals are done (green at the top) when every box is filled: blood pressure (both numbers), pulse, resp, temp, and O₂. */
const VITAL_BOXES = Object.keys(VITAL_RANGES) as (keyof typeof VITAL_RANGES)[];
const vitalsFilled = (v: ChartData[string] | undefined) => !!v && typeof v === "object" && !Array.isArray(v) && VITAL_BOXES.every((k) => String((v as Record<string, unknown>)[k] ?? "").trim() !== "");
const itemsLotted = (list: ChartItem[]) => list.every((i) => (i.ingredients?.length ? i.ingredients.every((g) => g.lotId || g.lot.trim()) : !!i.lot.trim()));

/** The visit's steps, in order, from the appointment, its chart, and its bill. */
export function visitSteps(a: AppointmentDetail, chart: ChartView | null, paid: boolean): VisitStep[] {
  const vitals = chart?.template.sections.flatMap((s) => s.fields).filter((f) => f.kind === "vitals") ?? [];
  const catalogFields = chart?.template.sections.flatMap((s) => s.fields).filter((f): f is Extract<ChartField, { kind: "catalog" }> => f.kind === "catalog") ?? [];
  const given = catalogFields.flatMap((f) => (Array.isArray(chart?.data[f.key]) ? (chart!.data[f.key] as ChartItem[]) : []));
  const list: Omit<VisitStep, "state">[] = [];
  const done = new Set<StepKey>();
  list.push({ key: "forms", label: "Forms", detail: a.finishedInBoulevard ? "In Boulevard" : !a.formsNeeded.length ? "All signed" : a.goAhead.forms ? "Reason given" : `${a.formsNeeded.length} to sign` });
  if (!a.formsNeeded.length || a.goAhead.forms) done.add("forms");
  if (a.gfe) {
    list.push({ key: "gfe", label: "GFE", detail: gfeOk(a.gfe.state) ? "Current" : a.goAhead.gfe ? "Reason given" : a.gfe.state === "pending" ? "Waiting on client" : "Needed" });
    if (gfeOk(a.gfe.state) || a.goAhead.gfe) done.add("gfe");
  }
  // Charted in Boulevard (a visit finished there): its chart counts too, alongside anything charted here.
  const bv = a.boulevardChart;
  // Nothing to chart (only recovery): no vitals, chart, or lots; the visit goes from forms to paid.
  const noChart = !a.needsChart && !chart;
  // Vitals only when the visit's chart takes them (an injection-only chart doesn't); before the chart's started, shown.
  if (!noChart && (!chart || vitals[0])) {
    list.push({ key: "pre", label: "Pre-vitals", detail: "Before starting" });
    if (vitals[0] && vitalsFilled(chart?.data[vitals[0].key])) done.add("pre");
  }
  list.push({ key: "treatment", label: "Treatment", detail: noChart ? "Nothing to chart" : "Lots to pick" });
  if ((chart && given.length && itemsLotted(given)) || noChart) done.add("treatment");
  if (!noChart && (!chart || vitals[1])) {
    list.push({ key: "post", label: "Post-vitals", detail: "Before they leave" });
    if (vitals[1] && vitalsFilled(chart?.data[vitals[1].key])) done.add("post");
  }
  if (!noChart) list.push({ key: "chart", label: "Chart signed", detail: chart?.status === "signed" ? "Signed" : "Nurse signs" });
  if (chart?.status === "signed") done.add("chart");
  if (bv)
    for (const k of ["pre", "treatment", "post", "chart"] as const)
      if (!done.has(k)) {
        done.add(k);
        const step = list.find((x) => x.key === k);
        if (step) step.detail = k === "chart" ? "Signed in Boulevard" : "Boulevard chart";
      }
  const review = chart && chart.review.status !== "not_needed" ? chart.review.status : null;
  if (review || (bv && bv.review !== "not_needed")) {
    const reviewed = review === "reviewed" || bv?.review === "reviewed";
    list.push({ key: "review", label: "MD review", detail: reviewed ? "Reviewed" : "Waiting" });
    if (reviewed) done.add("review");
  }
  list.push({ key: "paid", label: "Paid", detail: a.finishedInBoulevard ? "In Boulevard" : paid ? "Paid" : "Checkout" });
  if (paid || a.finishedInBoulevard) done.add("paid");
  const first = list.find((s) => !done.has(s.key))?.key;
  return list.map((s) => ({ ...s, state: done.has(s.key) ? "done" : s.key === first ? "now" : "later", detail: done.has(s.key) && s.key === "pre" ? vitalsLine(chart?.data[vitals[0]!.key]) : s.detail }));
}

const vitalsLine = (v: ChartData[string] | undefined) => {
  const o = (v ?? {}) as Record<string, string>;
  return [o.systolic && o.diastolic ? `${o.systolic}/${o.diastolic}` : "", o.pulse ? `${o.pulse} bpm` : ""].filter(Boolean).join(" · ") || "Done";
};

/** The row of steps: green when done, amber for the next one, grey after. */
export function VisitSteps({ steps, open, onPick, compact }: { steps: VisitStep[]; open?: StepKey | null; onPick?: (k: StepKey) => void; compact?: boolean }) {
  return (
    <nav aria-label="Visit steps" className="grid gap-2" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? 76 : 120}px, 1fr))` }}>
      {steps.map((s, i) => (
        <button
          key={s.key}
          type="button"
          onClick={() => onPick?.(s.key)}
          disabled={!onPick}
          className={cx(
            "flex flex-col items-start gap-1 rounded-xl border text-left",
            compact ? "p-2" : "min-h-[84px] p-3",
            s.state === "done" ? "border-ok/30 bg-ok-wash" : s.state === "now" ? "border-wait/50 bg-wait-wash" : "border-line bg-white",
            open === s.key && "ring-2 ring-ink",
            onPick && "cursor-pointer hover:border-ink-soft",
          )}
        >
          <span className={cx("grid size-6 place-items-center rounded-full text-sm font-extrabold", s.state === "done" ? "bg-ok text-white" : s.state === "now" ? "bg-wait text-white" : "bg-mist text-muted")} aria-hidden>
            {s.state === "done" ? "✓" : s.state === "now" ? "!" : i + 1}
          </span>
          <span className={cx("font-extrabold", compact ? "text-xs" : "text-sm")}>{s.label}</span>
          {!compact && <span className="text-xs text-muted">{s.detail}</span>}
          <span className="sr-only">{s.state === "done" ? "done" : s.state === "now" ? "next" : "later"}</span>
        </button>
      ))}
    </nav>
  );
}

/** The visit screen: steps across the top, the treatment drawn, one main button. Works on a laptop or an iPad. */
export function VisitPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { can } = useSession();
  const appt = useQuery({ queryKey: ["appointment", id], queryFn: () => get<AppointmentDetail>(`/api/appointments/${id}`), refetchInterval: 10_000 });
  const a = appt.data;
  const chartId = a?.charts.find((c) => c.templateKey !== "adverse")?.id ?? null;
  const chart = useQuery({ queryKey: ["chart", chartId], queryFn: () => get<ChartView>(`/api/charts/${chartId}`), enabled: !!chartId });
  const checkout = useQuery({ queryKey: ["checkout", id], queryFn: () => get<CheckoutView>(`/api/appointments/${id}/checkout`), enabled: !!a && !a.finishedInBoulevard && !["cancelled", "no_show"].includes(a.status) });
  const catalog = useCatalog();
  const c = chart.data ?? null;
  const paid = !!checkout.data?.bills.find((b) => b.appointmentId === id && b.status === "paid");
  const steps = a ? visitSteps(a, c, paid) : [];
  const [open, setOpen] = useState<StepKey | null>(null);
  useBackTitle(a ? `${displayName(a.client)}'s visit` : null);
  const shown = open ?? steps.find((s) => s.state === "now")?.key ?? null;

  // Chart edits made here: vitals save themselves a moment after the last change (as on the chart).
  const [draft, setDraft] = useState<ChartData>({});
  const [vitalsDirty, setVitalsDirty] = useState(false);
  // The fields typed on this screen: the vitals, and the IV times with them.
  const vitalKeys = useMemo(() => [...(c?.template.sections.flatMap((s) => s.fields).filter((f) => f.kind === "vitals") ?? []).map((f) => f.key), "timeIn", "attempts", "timeOut"], [c?.template]);
  const typing = useRef(false);
  useEffect(() => {
    // The chart came back from a save (or a change elsewhere): keep the vitals being typed.
    if (c) setDraft((cur) => (typing.current ? { ...c.data, ...Object.fromEntries(vitalKeys.filter((k) => cur[k] !== undefined).map((k) => [k, cur[k]!])) } : c.data));
  }, [c]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: (data: ChartData) => put<ChartView>(`/api/charts/${chartId}`, { data, baseUpdatedAt: c?.updatedAt }),
    onSuccess: (v) => qc.setQueryData(["chart", chartId], v),
  });
  useEffect(() => {
    if (!vitalsDirty || save.isPending) return;
    const t = setTimeout(() => {
      setVitalsDirty(false);
      save.mutate(draft);
    }, 1200);
    return () => clearTimeout(t);
  }, [draft, vitalsDirty, save.isPending]); // eslint-disable-line react-hooks/exhaustive-deps
  // Done with the vitals: save anything still waiting, then on to the next step.
  const doneVitals = () => {
    if (vitalsDirty) {
      setVitalsDirty(false);
      save.mutate(draft);
    }
    typing.current = false;
    setOpen(null);
  };

  const [reasons, setReasons] = useState<{ forms: string; gfe: string }>({ forms: "", gfe: "" });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["appointment", id] });
    await qc.invalidateQueries({ queryKey: ["calendar"] });
  };
  const status = useMutation({
    mutationFn: (to: AppointmentStatus) => post<AppointmentDetail>(`/api/appointments/${id}/status`, { status: to }),
    onSuccess: async (d) => {
      qc.setQueryData(["appointment", id], d);
      await refresh();
    },
  });
  // A finished visit that isn't paid yet goes back to In progress to be fixed (Modify appointment works again).
  const reopen = useMutation({
    mutationFn: (reason: string) => post<AppointmentDetail>(`/api/appointments/${id}/status`, { status: "active", reason }),
    onSuccess: async (d) => {
      qc.setQueryData(["appointment", id], d);
      await refresh();
    },
  });
  const askReopen = () => {
    const reason = window.prompt("Reopen this visit to change it before checkout. Why?")?.trim();
    if (reason) reopen.mutate(reason);
  };
  // Treating without signed forms or a current GFE: the reason, kept with the visit.
  const goAhead = useMutation({
    mutationFn: () => post<AppointmentDetail>(`/api/appointments/${id}/go-ahead`, { formsReason: reasons.forms.trim() || undefined, gfeReason: reasons.gfe.trim() || undefined }),
    onSuccess: (d) => qc.setQueryData(["appointment", id], d),
  });
  // Once they're here, the nurse's chart is ready to fill in; the visit is "in the chair" from the
  // first thing charted (no "Start visit" button).
  const chartStarted = useRef(false);
  const startChart = useMutation({
    mutationFn: () => post<ChartView>(`/api/appointments/${id}/charts`, {}),
    onSuccess: () => refresh(),
  });
  useEffect(() => {
    if (!a || chartId || chartStarted.current || !a.needsChart || !can("charts.write") || !["arrived", "active"].includes(a.status)) return;
    chartStarted.current = true;
    startChart.mutate();
  }, [a, chartId, can, startChart]);

  // What's drawn: the chart once there is one (it follows the booking, and has anything added during
  // the visit), else the booking.
  const lines = useMemo(() => {
    if (!a || !catalog.data) return [];
    const { items, formulary } = catalog.data;
    if (!c) return treatmentLines(visitItemIds(a), items, formulary);
    const ids = [...new Set(Object.values(c.data).flatMap((v) => (Array.isArray(v) && v.length && typeof v[0] === "object" ? (v as ChartItem[]) : [])).map((i) => i.catalogItemId).filter((x): x is string => !!x))];
    // The drip first, then what's added to it.
    const drip = (id: string) => (items.find((i) => i.id === id)?.effectiveChartSection === "cocktail" ? 0 : 1);
    return treatmentLines(ids.sort((x, y) => drip(x) - drip(y)), items, formulary);
  }, [a, c, catalog.data]);
  // Under the title: the booked add-ons, then anything the chart has that wasn't booked (added during the visit or at checkout).
  const extras = useMemo(() => {
    if (!a) return [];
    const added = c ? Object.values(c.data).flatMap((v) => (Array.isArray(v) && v.length && typeof v[0] === "object" ? (v as ChartItem[]) : [])).filter((i) => i.added).map((i) => i.name) : [];
    return [...a.addOns.map((x) => x.name), ...added];
  }, [a, c]);
  const [modifying, setModifying] = useState(false);
  const [swapping, setSwapping] = useState<string | null>(null);
  const edits = useTreatmentEdits({ a, c, draft, setDraft, catalogItems: catalog.data?.items ?? [] });
  // The × and Swap go on each item's first line; items with no recipe are listed under the drawing.
  const firstLine = new Set(lines.filter((l, i) => lines.findIndex((m) => m.itemId === l.itemId) === i).map((l) => l.key));
  const noLines = (a ? a.services.flatMap((s) => [{ id: s.serviceId, name: s.name }, ...s.addOns.map((x) => ({ id: x.id, name: x.name }))]) : []).filter((x) => !lines.some((l) => l.itemId === x.id));
  const [writingNote, setWritingNote] = useState(false);

  // Ticking off what's been added to the bag (or drawn up): saved on the chart's ingredients
  // (recovery, with nothing to draw up, on the chart line itself).
  const ingredientFor = (data: ChartData, l: TreatmentLine): { addedAt?: string | null } | null => {
    for (const v of Object.values(data)) {
      if (!Array.isArray(v) || !v.length || typeof v[0] !== "object") continue;
      for (const item of v as ChartItem[]) {
        if (item.catalogItemId !== l.itemId) continue;
        if (l.how === "recovery") return item;
        const g = item.ingredients?.find((x) => x.formularyItemId === l.formularyItemId);
        if (g) return g;
      }
    }
    return null;
  };
  const toggleAdded = useMutation({
    mutationFn: ({ list, added }: { list: TreatmentLine[]; added: boolean | null }) => {
      const data = structuredClone(draft);
      const now = new Date().toISOString();
      for (const l of list) {
        const g = ingredientFor(data, l);
        // null: flip one; true/false: set them all (the bag).
        if (g) g.addedAt = (added ?? !g.addedAt) ? (g.addedAt ?? now) : null;
      }
      setDraft(data);
      return put<ChartView>(`/api/charts/${chartId}`, { data, baseUpdatedAt: c?.updatedAt });
    },
    onSuccess: (v) => qc.setQueryData(["chart", chartId], v),
  });

  if (appt.isLoading) return <Loading />;
  if (!a) return <Alert>{errorMessage(appt.error)}</Alert>;
  const over = ["cancelled", "no_show"].includes(a.status);
  const nurse = can("charts.write");
  const formsOpen = a.formsNeeded.length > 0 && !a.goAhead.forms;
  const gfeOpen = !!a.gfe && !gfeOk(a.gfe.state) && !a.goAhead.gfe;
  const holdTreatment = formsOpen || gfeOpen;
  const primary: { label: string; run: () => void } | null =
    a.status === "unconfirmed" || a.status === "confirmed"
      ? { label: "Check in", run: () => status.mutate("arrived") }
      : a.status === "active" || a.status === "arrived"
          ? paid
            ? { label: "Complete visit", run: () => status.mutate("completed") }
            : { label: "Finish & check out", run: () => nav(`/checkout/${a.id}`) }
          : null;
  const fields = c?.template.sections.flatMap((s) => s.fields) ?? [];
  const vitals = fields.filter((f) => f.kind === "vitals");
  const vitalsField = shown === "pre" ? vitals[0] : shown === "post" ? vitals[1] : undefined;
  // With the vitals, the chart's IV times (the same fields as on the chart, so either place fills both).
  const STEP_EXTRAS: Partial<Record<StepKey, string[]>> = { pre: ["timeIn", "attempts"], post: ["timeOut"] };
  const ivTimes = (shown ? (STEP_EXTRAS[shown] ?? []) : []).map((k) => fields.find((f) => f.key === k)).filter((f): f is ChartField => !!f);
  const editable = !!c && c.status === "draft" && can("charts.write");

  return (
    <div className="mx-auto flex max-w-[1320px] flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <BackLink to="/front-desk" label="Front desk" className="text-sm font-bold text-brand" />
          <div>
            <h1 className="page-title">
              <Link to={`/clients/${a.client.id}`} className="hover:underline" title="Open their profile">
                {displayName(a.client)}
              </Link>
            </h1>
            <div className="mt-1 text-sm text-ink-soft">
              {a.client.dob ? `${ageOn(a.client.dob, a.start.slice(0, 10))} · ` : ""}
              {new Date(`${a.start.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {time12(a.start)}–{time12(a.end)} · {a.serviceName}
              {a.coveredBy ? ` · covered by ${a.coveredBy}` : ""}
              {` · ${a.staffName ?? "No nurse"}`}
            </div>
          </div>
          <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABELS[a.status]}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("appointments.manage") && a.status === "completed" && !paid && !a.finishedInBoulevard && (
            <Button variant="secondary" busy={reopen.isPending} onClick={askReopen}>
              Reopen visit
            </Button>
          )}
          {chartId && (
            <Button variant="secondary" onClick={() => nav(`/charts/${chartId}`)}>
              Full chart
            </Button>
          )}
          {can("charts.write", "appointments.manage") && (
            <Button variant="secondary" onClick={() => setWritingNote(true)}>
              School / work note
            </Button>
          )}
          {primary && !over && (
            <Button variant="primary" className="px-6" busy={status.isPending} onClick={primary.run}>
              {primary.label}
            </Button>
          )}
        </div>
      </header>
      {modifying && <EditAppointment a={a} when={!!c} onClose={() => setModifying(false)} />}
      {swapping && catalog.data && <SwapDrip a={a} from={swapping} catalogItems={catalog.data.items} edits={edits} onClose={() => setSwapping(null)} />}
      {writingNote && <NoteDialog appointmentId={a.id} clientId={a.client.id} onClose={() => setWritingNote(false)} />}
      <ExcuseNotesList clientId={a.client.id} appointmentId={a.id} title="Notes from this visit" />
      {a.client.schedulingAlert && <div className="rounded-[var(--radius-control)] bg-wait-wash px-3 py-2 text-sm font-semibold text-wait">⚠ {a.client.schedulingAlert}</div>}
      <VisitAlerts a={a} />
      {status.error && <Alert>{errorMessage(status.error)}</Alert>}
      {reopen.error && <Alert>{errorMessage(reopen.error)}</Alert>}
      {a.chartNeedsUpdate.length > 0 && chartId && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-control)] bg-wait-wash px-3 py-2 text-sm font-semibold text-wait">
          <span>Chart needs updating: {a.chartNeedsUpdate.join(", ")} added at checkout.</span>
          <Button onClick={() => nav(`/charts/${chartId}?update=1`)}>Update chart</Button>
        </div>
      )}
      {nurse && holdTreatment && !over && a.status !== "completed" && (
        <form
          className="flex flex-col gap-3 rounded-2xl border-2 border-wait/60 bg-white p-5"
          onSubmit={(e) => {
            e.preventDefault();
            goAhead.mutate();
          }}
        >
          <div>
            <h2 className="text-lg font-bold">Before treatment</h2>
            <p className="text-sm text-ink-soft">
              {[formsOpen ? "forms aren't all signed" : "", gfeOpen ? "the good faith exam isn't current" : ""].filter(Boolean).join(" and ").replace(/^./, (x) => x.toUpperCase())}. Finish them (the steps below), or give a reason to treat anyway. The chart can't be signed until one or the other.
            </p>
          </div>
          {formsOpen && <Field label={`Reason to treat without signed forms (${a.formsNeeded.map((f) => f.name).join(", ")})`} value={reasons.forms} onChange={(e) => setReasons({ ...reasons, forms: e.target.value })} />}
          {gfeOpen && <Field label="Reason to treat without a current good faith exam (the medical director cleared them)" value={reasons.gfe} onChange={(e) => setReasons({ ...reasons, gfe: e.target.value })} />}
          {goAhead.error && <Alert>{errorMessage(goAhead.error)}</Alert>}
          <div>
            <Button type="submit" variant="secondary" busy={goAhead.isPending} disabled={!(formsOpen && reasons.forms.trim()) && !(gfeOpen && reasons.gfe.trim())}>
              Save reason
            </Button>
          </div>
        </form>
      )}
      {(a.goAhead.forms || a.goAhead.gfe) && (
        <p className="text-sm text-ink-soft">
          {a.goAhead.forms && <span className="mr-4">Treating without signed forms: “{a.goAhead.forms}”</span>}
          {a.goAhead.gfe && <span>Treating without a current GFE: “{a.goAhead.gfe}”</span>}
        </p>
      )}

      {!over && (
        <div className="rounded-2xl border border-line bg-white p-3.5">
          <VisitSteps steps={steps} open={shown} onPick={(k) => (k === "treatment" ? document.getElementById("treatment")?.scrollIntoView({ behavior: "smooth", block: "start" }) : setOpen(k === shown ? null : k))} />
        </div>
      )}

      {!over && shown && shown !== "treatment" && (
        <section className={cx("flex flex-col gap-3 rounded-2xl border-2 bg-white p-5", steps.find((s) => s.key === shown)?.state === "done" ? "border-ok/40" : "border-wait/50")}>
          {shown === "forms" && (
            <>
              <h2 className="text-lg font-bold">{a.formsNeeded.length ? "Forms to sign" : "Forms: all signed"}</h2>
              {a.formsNeeded.length > 0 && <p className="text-sm text-ink-soft">{a.formsNeeded.map((f) => `${f.name}${f.state === "missing" ? "" : f.state === "expired" ? " (expired)" : " (updated, sign again)"}`).join(", ")}</p>}
              {a.formsNeeded.length > 0 && <FormsActions a={a} />}
              {(a.health.intakeSignedAt || a.health.consentSignedAt) && <HealthBlock h={a.health} />}
            </>
          )}
          {shown === "gfe" && a.gfe && <GfeVisitSection g={a.gfe} clientId={a.client.id} appointmentId={a.id} />}
          {a.boulevardChart && (shown === "pre" || shown === "post" || shown === "chart" || shown === "review") && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-soft">
                Charted in Boulevard{a.boulevardChart.review === "waiting" ? "; waiting for the medical director (Pending Charts)" : a.boulevardChart.review === "reviewed" ? "; reviewed by the medical director" : ""}.
              </p>
              <Button onClick={() => nav(`/records/${a.boulevardChart!.id}`)}>View Boulevard chart</Button>
            </div>
          )}
          {(shown === "pre" || shown === "post") && !c && !a.boulevardChart && (
            <p className="text-sm text-ink-soft">{a.status === "confirmed" || a.status === "unconfirmed" ? "Check them in to begin charting." : startChart.isPending ? "Getting the chart ready…" : "No chart yet."}</p>
          )}
          {(shown === "pre" || shown === "post") && c && vitalsField && (
            <ChartPanel
              title={shown === "pre" ? "Vitals before treatment" : "Vitals after treatment"}
              note="Saves as you type. Out-of-range numbers are flagged, never blocked."
              status={save.isPending ? "Saving…" : vitalsDirty ? "Editing" : save.isSuccess ? "Saved" : ""}
              error={save.error}
              editable={editable}
              onDone={doneVitals}
            >
              {[vitalsField, ...ivTimes].map((f, i) => (
                <div key={f.key} className={i ? "mt-4 max-w-xs" : undefined}>
                  <FieldView
                    f={f}
                    c={c}
                    data={draft}
                    editable={editable}
                    onChange={(k, v) => {
                      // Stay on this step while typing (it turns green at the top once every vitals box is filled).
                      typing.current = true;
                      if (open !== shown) setOpen(shown);
                      setDraft({ ...draft, [k]: v });
                      setVitalsDirty(true);
                    }}
                  />
                </div>
              ))}
            </ChartPanel>
          )}
          {shown === "chart" && (c || !a.boulevardChart) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">{c?.status === "signed" ? "Chart signed" : "Chart"}</h2>
                <p className="text-sm text-ink-soft">{c ? (c.status === "signed" ? `Signed by ${c.signedBy}.` : c.missing.length ? `Still to fill in: ${c.missing.join(", ")}.` : "Everything's filled in. Review and sign.") : "Start the visit to begin charting."}</p>
              </div>
              {c && <Button variant="primary" onClick={() => nav(`/charts/${c.id}`)}>{c.status === "signed" ? "View chart" : "Open chart to sign"}</Button>}
            </div>
          )}
          {shown === "review" && c && (
            <p className="text-sm text-ink-soft">{c.review.status === "reviewed" ? `Reviewed by ${c.review.by}.` : "Waiting for the medical director (Pending Charts)."}</p>
          )}
          {shown === "paid" && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">{a.finishedInBoulevard ? "Checked out in Boulevard" : paid ? "Paid" : "Checkout"}</h2>
                {a.finishedInBoulevard ? <p className="text-sm text-ink-soft">Paid in Boulevard. Nothing to charge here.</p> : a.coveredBy && <p className="text-sm text-ink-soft">Covered by {a.coveredBy}.</p>}
                <VisitMoney a={a} />
                {checkout.data && <BillSummary co={checkout.data} appointmentId={a.id} />}
              </div>
              {a.boulevardReceipt && <BoulevardReceipt r={a.boulevardReceipt} className="w-full max-w-md" />}
              {!a.finishedInBoulevard && (
                <Button variant={paid ? "secondary" : "primary"} onClick={() => nav(`/checkout/${a.id}`)}>
                  {paid ? "View checkout" : "Go to checkout"}
                </Button>
              )}
            </div>
          )}
        </section>
      )}

      {!over && (
      <section id="treatment" className="flex scroll-mt-4 flex-col gap-4 rounded-[20px] border border-line bg-white p-6">
        {c && holdTreatment && a.status !== "completed" && <Alert>Don't treat yet: {formsOpen ? "forms aren't signed" : "the good faith exam isn't current"}. See "Before treatment" above.</Alert>}
        <div>
          <div className="text-xs font-extrabold uppercase tracking-[.08em] text-muted">Treatment</div>
          <h2 className="mt-1 font-display text-[28px] leading-tight">
            {a.serviceName}
            {extras.length > 0 && <span className="ml-2 font-sans text-[15px] text-ink-soft">+ {extras.join(" + ")}</span>}
          </h2>
        </div>
        {catalog.isLoading ? (
          <Loading />
        ) : (
          <TreatmentDrawing
            lines={lines}
            tracking={
              {
                addedAt: (l) => (c ? (ingredientFor(c.status === "draft" ? draft : c.data, l)?.addedAt ?? null) : null),
                // Signed: everything on the chart was given, so nothing shows as still to add.
                // The bag of fluid has its lot but isn't ticked off (it goes up with everything in it).
                tracks: (l) => l.how !== "base" && c?.status === "draft" && !!ingredientFor(draft, l),
                onToggle: editable ? (l) => ingredientFor(draft, l) && toggleAdded.mutate({ list: [l], added: null }) : undefined,
                onSetMany: editable ? (list, added) => toggleAdded.mutate({ list, added }) : undefined,
                hint: !c ? (a.needsChart ? "Check them in to chart: each medication's lot, and what's added." : "Nothing on this visit needs a chart.") : c.status === "signed" ? "The chart is signed." : "Only nurses who chart can tick these off.",
              } satisfies AddedTracking
            }
            rowExtra={(l) => <LineControls l={l} first={firstLine.has(l.key)} c={c} draft={draft} edits={edits} onSwap={setSwapping} />}
            footer={
              <div className="flex flex-col gap-2">
                {noLines.length > 0 && (
                  <p className="text-sm text-ink-soft">
                    Also on this visit (nothing to chart):{" "}
                    {noLines.map((x, i) => (
                      <span key={x.id}>
                        {i > 0 && ", "}
                        {x.name}
                        {edits.canChange && edits.manage && !edits.drips.includes(x.id) && (
                          <button type="button" className="ml-1 text-muted hover:text-danger" aria-label={`Take ${x.name} off`} disabled={edits.busy} onClick={() => edits.remove.mutate({ id: x.id, name: x.name })}>
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </p>
                )}
                {c ? (
                  <TreatmentAdds c={c} draft={draft} edits={edits} menu={a.eventMenuIds} />
                ) : (
                  <p className="text-sm text-muted">{a.needsChart ? "Check them in to add to the bag or give an injection here; before that, use Edit booking below." : "To add a drip or an injection, use Edit booking below (the chart starts then)."}</p>
                )}
                {!!(edits.remove.error || edits.add.error) && !c && <Alert>{errorMessage(edits.remove.error ?? edits.add.error)}</Alert>}
              </div>
            }
          />
        )}
        {toggleAdded.error && <Alert>{errorMessage(toggleAdded.error)}</Alert>}
        <p className="text-sm text-muted">Colours match the vials on the Inventory shelf.</p>
      </section>
      )}

      {over && (
        <section className="rounded-[20px] border border-line bg-white p-5">
          <VisitMoney a={a} />
        </section>
      )}
      <VisitDetails
        a={a}
        onEdit={can("appointments.manage") && !over && a.status !== "completed" && !paid && !a.managedInBoulevard ? () => setModifying(true) : undefined}
        editLabel={c ? "Edit time or nurse" : "Edit booking"}
      />
      <div className="flex flex-wrap items-center gap-2">
        {a.charts
          .filter((x) => x.templateKey === "adverse")
          .map((x) => (
            <Button key={x.id} variant="quiet" onClick={() => nav(`/charts/${x.id}`)}>
              Adverse reaction report
            </Button>
          ))}
        {can("appointments.manage") && <VisitEndActions a={a} />}
      </div>
      <VisitHistory a={a} />
    </div>
  );
}

function ChartPanel({ title, note, children, status, error, editable, onDone }: { title: string; note: string; children: React.ReactNode; status: string; error: unknown; editable: boolean; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">{title}</h2>
        <span className="text-sm text-muted">{note}</span>
      </div>
      {children}
      {!!error && <Alert>{errorMessage(error)}</Alert>}
      {editable ? (
        <div className="flex items-center gap-3">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
          <span className="text-sm text-muted">{status}</span>
        </div>
      ) : (
        <p className="text-sm text-muted">The chart is signed; changes go in an addendum.</p>
      )}
    </div>
  );
}

/** Getting forms signed: on a clinic iPad, on this screen, or the client's own link. */
export function FormsActions({ a }: { a: AppointmentDetail }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => get<DeviceView[]>("/api/devices"), refetchInterval: 5_000 });
  const paired = devices.data?.filter((d) => d.paired) ?? [];
  const send = useMutation({ mutationFn: (deviceId: string) => post<DeviceView[]>(`/api/appointments/${a.id}/send-forms`, { deviceId }), onSuccess: (d) => qc.setQueryData(["devices"], d) });
  const clear = useMutation({ mutationFn: (deviceId: string) => post<DeviceView[]>(`/api/devices/${deviceId}/clear`, {}), onSuccess: (d) => qc.setQueryData(["devices"], d) });
  const text = useMutation({ mutationFn: () => post<{ to: string; sms: boolean; email: boolean }>(`/api/appointments/${a.id}/forms-link/send`, {}) });
  const here = paired.find((d) => d.busyWith?.appointmentId === a.id);
  return (
    <div className="flex flex-col gap-2">
      {paired.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-ink-soft">Send to</span>
          {paired.map((d) => {
            const mine = d.busyWith?.appointmentId === a.id;
            return (
              <Button
                key={d.id}
                variant={mine ? "primary" : "secondary"}
                busy={send.isPending && send.variables === d.id}
                onClick={() => (mine ? undefined : (!d.busyWith || window.confirm(`${d.name} is showing ${d.busyWith.clientName}'s forms. Send these instead?`)) && send.mutate(d.id))}
              >
                {d.name}
                <span className={`ml-1.5 inline-block size-2 rounded-full ${d.online ? "bg-ok" : "bg-muted"}`} aria-label={d.online ? "online" : "offline"} />
              </Button>
            );
          })}
        </div>
      )}
      {here && (
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
          <span>
            Showing on <b>{here.name}</b>{here.online ? "" : " (it's offline: check it's on and the page is open)"}. This updates when they're signed.
          </span>
          <Button variant="quiet" busy={clear.isPending} onClick={() => clear.mutate(here.id)}>
            Take back
          </Button>
        </div>
      )}
      {(send.error || clear.error || text.error) && <Alert>{errorMessage(send.error ?? clear.error ?? text.error)}</Alert>}
      <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={() => nav(`/kiosk/${a.id}`)}>
        Fill in on this screen
      </Button>
      <Button variant="quiet" busy={text.isPending} disabled={text.isSuccess} onClick={() => text.mutate()}>
        {text.data ? `Sent to ${text.data.to}${text.data.sms ? "" : " (email)"}` : "Text them the link"}
      </Button>
      <Button variant="quiet" onClick={() => void navigator.clipboard?.writeText(a.formsLink).then(() => setCopied(true))}>
        {copied ? "Link copied" : "Copy client's link"}
      </Button>
      </div>
    </div>
  );
}

/** The visit's bill at a glance (Paid step): what was charged, discounts, tip, and how it was paid. */
function BillSummary({ co, appointmentId }: { co: CheckoutView; appointmentId: string }) {
  const b = co.bills.find((x) => x.appointmentId === appointmentId);
  if (!b || !b.lines.length) return null;
  const off = b.discountApplied === "plan" ? b.planDiscountCents : b.discountApplied === "manual" ? b.discountCents : 0;
  const rows: { label: string; amount: string; sub?: boolean }[] = [
    ...b.lines.map((l) => ({ label: `${l.quantity > 1 ? `${l.quantity} × ` : ""}${l.name}`, amount: l.coveredBy ? `Covered by ${l.coveredBy}` : formatCents(l.amountCents) })),
    ...(off ? [{ label: b.discountApplied === "plan" ? "Member pricing" : `Discount${b.discountReason ? ` (${b.discountReason})` : ""}`, amount: `−${formatCents(off)}`, sub: true }] : []),
    ...(b.depositCreditCents ? [{ label: "Deposit paid", amount: `−${formatCents(b.depositCreditCents)}`, sub: true }] : []),
    ...(b.tipCents ? [{ label: "Tip", amount: formatCents(b.tipCents) }] : []),
  ];
  // Money in for this booking (a payment can cover several people's bills).
  const paid = co.payments.filter((p) => (p.kind === "checkout" || p.kind === "deposit") && p.status === "succeeded");
  return (
    <div className="mt-3 flex max-w-md flex-col gap-1 text-sm">
      {rows.map((r, i) => (
        <div key={i} className={cx("flex justify-between gap-4", r.sub && "text-ok")}>
          <span>{r.label}</span>
          <span className="tabular-nums">{r.amount}</span>
        </div>
      ))}
      <div className="mt-1 flex justify-between gap-4 border-t border-line pt-1 font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{formatCents(b.totalCents)}</span>
      </div>
      {b.balanceCents > 0 && (
        <div className="flex justify-between gap-4 font-semibold text-wait">
          <span>Still to pay</span>
          <span className="tabular-nums">{formatCents(b.balanceCents)}</span>
        </div>
      )}
      {paid.map((p) => (
        <div key={p.id} className="flex justify-between gap-4 text-ink-soft">
          <span>
            {p.kind === "deposit" ? "Deposit · " : ""}
            {PAYMENT_METHOD_LABELS[p.method]}
            {p.cardLast4 ? ` •••• ${p.cardLast4}` : ""} · {formatDateTime(p.at)}
            {p.refundedCents > 0 && <span className="text-danger"> · {formatCents(p.refundedCents)} refunded</span>}
          </span>
          <span className="tabular-nums">{formatCents(p.amountCents)}</span>
        </div>
      ))}
    </div>
  );
}
